import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_URL = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  register: (data) => api.post('/auth/register', data),
  getMe: () => api.get('/auth/me'),
};

// CMS API
export const cmsAPI = {
  getSections: (page) => api.get('/cms/sections', { params: { page } }),
  getSection: (id) => api.get(`/cms/sections/${id}`),
  updateSection: (id, data) => api.put(`/cms/sections/${id}`, data),
  createSection: (data) => api.post('/cms/sections', data),
  deleteSection: (id) => api.delete(`/cms/sections/${id}`),
  seedContent: () => api.post('/cms/seed'),
  reset: () => api.post('/cms/reset'),
};

// Settings API
export const settingsAPI = {
  get: () => api.get('/settings'),
  update: (data) => api.put('/settings', data),
};

// Inscriptions API
export const inscriptionsAPI = {
  create: (data) => api.post('/inscriptions', data),
  list: () => api.get('/inscriptions'),
  get: (id) => api.get(`/inscriptions/${id}`),
  updateStatus: (id, status) => api.put(`/inscriptions/${id}/status`, null, { params: { status } }),
  getPDF: (id) => `${API_URL}/inscriptions/${id}/pdf`,
  /**
   * Download the ZIP (PDF recap + all attachments) — admin only.
   * Uses axios so the JWT token is sent, then triggers a blob download.
   */
  downloadZip: async (id, numero = '') => {
    const res = await api.get(`/inscriptions/${id}/zip`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/zip' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `inscription_${numero || id}_complet.zip`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => window.URL.revokeObjectURL(url), 1000);
  },
};

// Tarifs API
export const tarifsAPI = {
  list: () => api.get('/tarifs'),
  create: (data) => api.post('/tarifs', data),
  update: (id, data) => api.put(`/tarifs/${id}`, data),
  delete: (id) => api.delete(`/tarifs/${id}`),
};

// Communications API
export const communicationsAPI = {
  list: (publishedOnly = true) => api.get('/communications', { params: { published_only: publishedOnly } }),
  create: (data) => api.post('/communications', data),
  update: (id, data) => api.put(`/communications/${id}`, data),
  delete: (id) => api.delete(`/communications/${id}`),
};

// Contact API
export const contactAPI = {
  submit: (data) => api.post('/contact', data),
  list: () => api.get('/contact/messages'),
  markRead: (id) => api.put(`/contact/messages/${id}/read`),
  sendDevis: (data) => api.post('/contact/devis', data),
  sendDemandeInfo: (data) => api.post('/contact/demande-info', data),
};

// Files API
export const filesAPI = {
  upload: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getUrl: (id) => `${API_URL}/files/${id}`,
};

// Admin API
export const adminAPI = {
  getStats: () => api.get('/admin/stats'),
};

// Devis API
export const devisAPI = {
  list: () => api.get('/contact/devis'),
  submit: (data) => api.post('/contact/devis', data),
  updateStatus: (id, status) => api.put(`/contact/devis/${id}/status`, { status }),
};

// Analytics API
export const analyticsAPI = {
  trackClick: (type) => api.post('/analytics/track/click', { type }),
  trackVisit: () => api.post('/analytics/track/visit'),
  getStats: () => api.get('/analytics/stats'),
  getDailyVisits: (days = 30) => api.get(`/analytics/visits-daily?days=${days}`),
};

// Messages API (Messagerie Interne)
export const messagesAPI = {
  getAll: () => api.get('/messages'),
  getInbox: () => api.get('/messages/inbox'),
  getSent: () => api.get('/messages/sent'),
  getUnreadCount: () => api.get('/messages/unread-count'),
  get: (id) => api.get(`/messages/${id}`),
  send: (data) => api.post('/messages', data),
  markRead: (id) => api.put(`/messages/${id}/read`),
  delete: (id) => api.delete(`/messages/${id}`),
  getContacts: () => api.get('/messages/contacts'),
};

// Blog API
export const blogAPI = {
  // Categories
  getCategories: () => api.get('/blog/categories'),
  createCategory: (data) => api.post('/blog/categories', data),
  updateCategory: (id, data) => api.put(`/blog/categories/${id}`, data),
  deleteCategory: (id) => api.delete(`/blog/categories/${id}`),
  
  // Articles
  getArticles: (params) => api.get('/blog/articles', { params }),
  getFeatured: (limit = 3) => api.get('/blog/articles/featured', { params: { limit } }),
  getArticle: (idOrSlug) => api.get(`/blog/articles/${idOrSlug}`),
  createArticle: (data) => api.post('/blog/articles', data),
  updateArticle: (id, data) => api.put(`/blog/articles/${id}`, data),
  deleteArticle: (id) => api.delete(`/blog/articles/${id}`),
  toggleFeatured: (id) => api.post(`/blog/articles/${id}/toggle-featured`),
};

export default api;
